<template>
  <v-container class="pa-0" :class="{ 'dki-mobile': mobile }">
    <!-- Desktop shell (unchanged) -->
    <v-container v-if="!mobile" class="wrapper pa-3">
      <v-btn color="primary" outlined @click="openPanelsList" :loading="loading">
        <span>{{ $t("add-new-panel") }}</span>
        <v-icon size="18" class="mr-1">mdi-plus</v-icon>
      </v-btn>
      <v-container class="pa-0 mt-5 columns">
        <v-container
          class="pa-4 column-panel d-flex justify-space-between align-center mt-2"
          v-for="(panel, index) in addedPanels"
          :key="index"
        >
          <div class="d-flex align-center gap-3">
            <v-card-text class="pa-0 text-caption">{{ index + 1 }}</v-card-text>
            <v-card-text class="pa-0 text-body-2">{{
              panel.panel_name || panel.name
            }}</v-card-text>
          </div>
          <div class="d-flex align-center gap-8">
            <v-card-text class="pa-0 text-body-2 mr-9"
              >{{ panel.productVariant || panel.sazito_product.length }}
              {{ $t("product-id") }}</v-card-text
            >
            <div @click="openTableDialog('edit', panel, index)">
              <svg
                style="cursor: pointer"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.66602 14.5002H13.3327M3.77668 9.29156C3.49243 9.57645 3.33275 9.96244 3.33268 10.3649V12.5002H5.48135C5.88402 12.5002 6.27002 12.3402 6.55468 12.0549L12.888 5.71822C13.1722 5.43329 13.3317 5.0473 13.3317 4.64489C13.3317 4.24248 13.1722 3.85649 12.888 3.57156L12.2627 2.94489C12.1217 2.80379 11.9542 2.69187 11.7699 2.61554C11.5856 2.5392 11.388 2.49994 11.1885 2.5C10.9891 2.50006 10.7915 2.53944 10.6073 2.6159C10.423 2.69235 10.2556 2.80437 10.1147 2.94556L3.77668 9.29156Z"
                  stroke="black"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </v-container>
      </v-container>
    </v-container>

    <!-- Mobile shell -->
    <div v-else class="dki-mobile__shell">
      <button
        type="button"
        class="dki-mobile__add"
        :disabled="loading"
        @click="openPanelsList"
      >
        <v-progress-circular
          v-if="loading"
          indeterminate
          size="18"
          width="2"
          color="#5A6B85"
        />
        <v-icon v-else size="18" color="#5A6B85">mdi-plus</v-icon>
        <span>{{ $t("add-new-panel") }}</span>
      </button>
      <div
        v-if="!(addedPanels && addedPanels.length)"
        class="dki-mobile__empty"
      >
        <v-icon size="28" color="#90A4AE">mdi-view-grid-plus-outline</v-icon>
        <div class="dki-mobile__empty-title">
          {{ $t("no panel founded!") }}
        </div>
        <div class="dki-mobile__empty-hint">
          {{ $t("add-new-panel") }}
        </div>
      </div>
      <div
        v-for="(panel, index) in addedPanels"
        :key="'p3-panel-' + index"
        class="dki-mobile__panel-card"
        @click="openTableDialog('edit', panel, index)"
      >
        <div class="dki-mobile__panel-main">
          <span class="dki-mobile__panel-index">{{ index + 1 }}</span>
          <div class="dki-mobile__panel-titles">
            <span class="dki-mobile__panel-name">{{
              panel.panel_name || panel.name
            }}</span>
            <span class="dki-mobile__panel-meta">
              {{
                panel.productVariant ||
                (panel.sazito_product && panel.sazito_product.length) ||
                0
              }}
              {{ $t("product-id") }}
            </span>
          </div>
        </div>
        <v-icon size="18" color="#78909C">mdi-pencil-outline</v-icon>
      </div>
    </div>

    <!-- Desktop dialogs (unchanged) -->
    <template v-if="!mobile">
    <!-- panels list-->
    <v-dialog
      v-model="panelsListDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="550"
    >
      <v-card class="pa-5">
        <v-container class="pa-0 d-flex align-center justify-space-between mb-7">
          <v-card-text class="pa-0 text-body-1">{{ $t("select-panel") }}</v-card-text>
          <v-icon style="cursor: pointer" @click="panelsListDialog = false"
            >mdi-close</v-icon
          >
        </v-container>
        <div v-if="items.length > 0">
          <v-card-text class="pa-0 text-body-2">{{
            $t("To connect to this product, choose from the panels below")
          }}</v-card-text>
          <v-container class="pa-0 mt-5 columns">
            <v-container
              class="pa-4 d-flex justify-space-between align-center mt-2"
              v-for="(item, index) in items"
              :key="index"
              @click="selectPanel(item)"
              :class="{
                'column-panel': !item.isSelected,
                'column-panel-disabled': item.isSelected,
              }"
            >
              <div class="d-flex align-center gap-3">
                <v-card-text
                  class="pa-0 text-caption"
                  :style="{ color: item.isSelected ? 'silver' : '' }"
                >
                  {{ index + 1 }}
                </v-card-text>
                <v-card-text
                  class="pa-0 text-body-2"
                  :style="{ color: item.isSelected ? 'silver' : '' }"
                >
                  {{ item.panel_name }}
                </v-card-text>
              </div>
              <div class="d-flex align-center gap-5">
                <v-card-text
                  v-if="!item.isSelected"
                  class="pa-0 text-body-2"
                  :style="{
                    color: item.panel_status == 'active' ? '#137333' : '#ff5252',
                  }"
                >
                  {{ $t(item.panel_status) }}
                </v-card-text>
                <v-card-text v-else class="pa-0 text-body-2" style="color: silver">
                  {{ $t(item.panel_status) }}
                </v-card-text>
              </div>
            </v-container>
          </v-container>
        </div>
        <div v-else>
          <v-card-text class="pa-0 text-body-1">
            {{ $t("no panel founded!") }}
            {{ $t("first add panels in integrator setting") }}
          </v-card-text>
        </div>
      </v-card>
    </v-dialog>
    <!--table-->
    <v-dialog
      v-model="tableDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="800"
    >
      <v-card class="pa-5">
        <v-container class="pa-0 d-flex justify-space-between align-center">
          <v-card-text
            v-if="selectedPanel != null && tableDialogType == 'add'"
            class="pa-0 text-body-2"
            >{{ $t("product-infomation") }}
            {{ selectedPanel.panel_name || selectedPanel.name }}</v-card-text
          >
          <v-card-text
            v-if="selectedPanel != null && tableDialogType == 'edit'"
            class="pa-0 text-body-2"
            >{{ $t("edit-product") }}
            {{ selectedPanel.panel_name || selectedPanel.name }}</v-card-text
          >
          <v-icon size="20" @click="tableDialog = false">mdi-close</v-icon>
        </v-container>
        <v-container class="pa-0 d-flex justify-space-between align-center mt-9 mb-3">
          <v-btn color="secondary" outlined @click="openProductDialog('add')">
            <span class="ml-2">{{ $t("add-new-portal-id") }}</span>
            <v-icon size="16">mdi-plus</v-icon>
          </v-btn>
          <div class="d-flex align-center gap-2">
            <v-btn color="success" outlined @click="activeAllProduct">
              {{ $t("active-all") }}
            </v-btn>
            <v-btn color="error" outlined @click="deactiveAllProduct">
              {{ $t("deactive-all") }}
            </v-btn>
          </div>
        </v-container>
        <v-data-table
          :headers="tableHeaders"
          :items="tableItems"
          :items-per-page="-1"
          hide-default-footer
          :height="isMobile || isTablet ? 265 : 265"
          fixed-header
          :class="{ 'ma-5': isMobile }"
          class="product"
        >
          <template v-slot:header.portal-id="{ header }">
            <span class="ml-5">#</span>
            <span>{{ header.text }}</span>
          </template>
          <template v-slot:header.portal-dynamic-id="{ header }">
            <span class="ml-5">#</span>
            <span>{{ header.text }}</span>
          </template>
          <template v-slot:item.portal-id="{ item, index }">
            <v-container class="pa-0 d-flex align-center gap-2">
              <span style="width: 20px">{{ index + 1 }}</span>
              <v-card-text class="pa-0 text-body-2">{{ item.portal_id }}</v-card-text>
            </v-container>
          </template>
          <template v-slot:item.portal-dynamic-id="{ item, index }">
            <v-container class="pa-0 d-flex align-center gap-2">
              <span style="width: 20px">{{ index + 1 }}</span>
              <v-card-text class="pa-0 text-body-2">{{
                item.portal_dynamic_id
              }}</v-card-text>
            </v-container>
          </template>
          <template v-slot:item.other="{ item }">
            <v-card-text
              class="pa-0 text-caption"
              :style="{
                color: item.status == 'active' ? 'rgb(19, 115, 51)' : 'rgb(255, 82, 82)',
              }"
            >
              {{ $t(item.status) }}
            </v-card-text>
          </template>
          <template v-slot:header.edit="{ header }">
            <v-container class="pa-0 d-flex align-center ma-0 gap-3">
              {{ header.text }}
              <div @click="openDeleteProductDialog('all')" style="cursor: pointer">
                <svg
                  class="mt-1"
                  width="15"
                  height="15"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.625 3.375H5.5C5.56875 3.375 5.625 3.31875 5.625 3.25V3.375ZM5.625 3.375H10.375V3.25C10.375 3.31875 10.4312 3.375 10.5 3.375H10.375V4.5H11.5V3.25C11.5 2.69844 11.0516 2.25 10.5 2.25H5.5C4.94844 2.25 4.5 2.69844 4.5 3.25V4.5H5.625V3.375ZM13.5 4.5H2.5C2.22344 4.5 2 4.72344 2 5V5.5C2 5.56875 2.05625 5.625 2.125 5.625H3.06875L3.45469 13.7969C3.47969 14.3297 3.92031 14.75 4.45312 14.75H11.5469C12.0813 14.75 12.5203 14.3313 12.5453 13.7969L12.9312 5.625H13.875C13.9438 5.625 14 5.56875 14 5.5V5C14 4.72344 13.7766 4.5 13.5 4.5ZM11.4266 13.625H4.57344L4.19531 5.625H11.8047L11.4266 13.625Z"
                    fill="black"
                  ></path>
                </svg>
              </div>
            </v-container>
          </template>
          <template v-slot:item.edit="{ item, index }">
            <v-container class="pa-0 d-flex align-center ma-0 justify-space-between">
              <div
                style="cursor: pointer"
                class="pr-2"
                @click="openProductDialog('edit', item)"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.66797 14.5002H13.3346M3.77864 9.29156C3.49438 9.57645 3.33471 9.96244 3.33464 10.3649V12.5002H5.4833C5.88597 12.5002 6.27197 12.3402 6.55664 12.0549L12.89 5.71822C13.1741 5.43329 13.3337 5.0473 13.3337 4.64489C13.3337 4.24248 13.1741 3.85649 12.89 3.57156L12.2646 2.94489C12.1236 2.80379 11.9562 2.69187 11.7718 2.61554C11.5875 2.5392 11.39 2.49994 11.1905 2.5C10.991 2.50006 10.7935 2.53944 10.6092 2.6159C10.425 2.69235 10.2576 2.80437 10.1166 2.94556L3.77864 9.29156Z"
                    stroke="black"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </div>
              <div
                @click="openDeleteProductDialog('single', item, index)"
                style="cursor: pointer"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.625 3.375H5.5C5.56875 3.375 5.625 3.31875 5.625 3.25V3.375ZM5.625 3.375H10.375V3.25C10.375 3.31875 10.4312 3.375 10.5 3.375H10.375V4.5H11.5V3.25C11.5 2.69844 11.0516 2.25 10.5 2.25H5.5C4.94844 2.25 4.5 2.69844 4.5 3.25V4.5H5.625V3.375ZM13.5 4.5H2.5C2.22344 4.5 2 4.72344 2 5V5.5C2 5.56875 2.05625 5.625 2.125 5.625H3.06875L3.45469 13.7969C3.47969 14.3297 3.92031 14.75 4.45312 14.75H11.5469C12.0813 14.75 12.5203 14.3313 12.5453 13.7969L12.9312 5.625H13.875C13.9438 5.625 14 5.56875 14 5.5V5C14 4.72344 13.7766 4.5 13.5 4.5ZM11.4266 13.625H4.57344L4.19531 5.625H11.8047L11.4266 13.625Z"
                    fill="black"
                  ></path>
                </svg>
              </div>
            </v-container>
          </template>
        </v-data-table>
        <v-container class="pa-0 d-flex align-center gap-2 mt-9">
          <v-btn color="primary" height="32" @click="processPanel">
            {{ tableDialogType == "add" ? $t("confirm") : $t("edit") }}
          </v-btn>
          <v-btn color="secondary" height="32" outlined @click="tableDialog = false">
            {{ $t("cancel") }}
          </v-btn>
        </v-container>
      </v-card>
    </v-dialog>
    <!--product-->
    <v-dialog
      v-model="productDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="800"
    >
      <v-card class="pa-5">
        <v-form ref="form" v-model="valid">
          <v-container class="pa-0 d-flex justify-space-between">
            <v-card-text class="pa-0 text-body-1">{{
              productDialogType == "add" ? $t("add-new-variant") : $t("edit")
            }}</v-card-text>
            <v-icon size="20" @click="productDialog = false">mdi-close</v-icon>
          </v-container>
          <v-card-text class="pa-0 text-body-2 mt-6">{{
            $t("general-setting")
          }}</v-card-text>
          <v-container class="pa-0 d-flex justify-space-between align-center mt-4">
            <div class="d-flex align-center gap-3" style="width: 360px">
              <v-text-field
                v-model="newProduct.portal_id"
                :label="$t('portalId')"
                outlined
                height="32"
                dense
                clearable
                style="width: 45px"
                :rules="[(v) => !!v || $t('is-required')]"
              />
              <v-text-field
                v-model="newProduct.portal_dynamic_id"
                :label="$t('portalVariantId')"
                outlined
                height="32"
                dense
                clearable
                style="width: 45px"
              />
            </div>
            <v-container
              class="wrapper-toggle d-flex align-center pa-0 ma-0"
              style="width: 151px"
            >
              <div
                v-for="(item, index) in activatorOptions"
                :key="index"
                @click="selectStatusProduct(item)"
                class="child flex-fill text-center"
                :style="{
                  'background-color':
                    selectedStatusProduct == item.value ? '#E5FEED' : '',
                  'border-color':
                    selectedStatusProduct == item.value ? '#0066ff' : 'silver',
                  color: selectedStatusProduct == item.value ? '#137333' : '',
                }"
              >
                <v-card-text class="pa-0 text-caption">{{ item.label }}</v-card-text>
              </div>
            </v-container>
          </v-container>
          <v-container class="pa-0 d-flex justify-space-between align-center mt-9">
            <div class="d-flex align-center gap-2">
              <v-btn color="primary" height="32" @click="checkValidation">
                {{ productDialogType == "add" ? $t("add") : $t("edit") }}
              </v-btn>
              <v-btn
                color="secondary"
                outlined
                height="32"
                @click="productDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </div>
          </v-container>
        </v-form>
      </v-card>
    </v-dialog>
    <!--delete dialog-->
    <v-dialog
      v-if="deleteItem != null"
      v-model="deleteProductDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="350"
    >
      <v-card class="pa-5">
        <v-card-text class="pa-0 text-body-2">
          {{
            deleteTpye == "single"
              ? $t("Are you sure you want to delete this item ?")
              : $t("are you sure you want to delete all items?")
          }}
        </v-card-text>
        <v-container class="pa-0" v-if="deleteTpye == 'single'">
          <v-container class="pa-0 d-flex align-center gap-1 mt-5">
            <v-card-text class="pa-0 text-body-2">{{ $t("sazitoId") }}: </v-card-text>
            <v-card-text class="pa-0 text-caption">{{
              deleteItem.sazito_id
            }}</v-card-text>
          </v-container>
          <v-container class="pa-0 d-flex align-center gap-1 mt-3">
            <v-card-text class="pa-0 text-body-2"
              >{{ $t("sazitoVariantId") }}:
            </v-card-text>
            <v-card-text class="pa-0 text-caption">{{
              deleteItem.portal_dynamic_id
            }}</v-card-text>
          </v-container>
          <v-container class="pa-0 d-flex align-center gap-1 mt-2">
            <v-card-text class="pa-0 text-body-2">{{ $t("status") }} : </v-card-text>
            <v-card-text class="pa-0 text-caption">{{
              $t(deleteItem.status)
            }}</v-card-text>
          </v-container>
        </v-container>
        <v-container class="pa-0 d-flex align-center gap-2 mt-6">
          <v-btn color="error" height="32" @click="deleteProduct">
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            color="secondary"
            height="32"
            outlined
            @click="deleteProductDialog = false"
          >
            {{ $t("no") }}
          </v-btn>
        </v-container>
      </v-card>
    </v-dialog>
    </template>

    <!-- Mobile sheets: same flags/methods as desktop dialogs -->
    <template v-else>
      <!-- Select panel -->
      <v-bottom-sheet v-model="panelsListDialog">
        <v-card class="dki-sheet" dir="rtl">
          <div class="dki-sheet__handle" />
          <div class="dki-sheet__header">
            <span class="dki-sheet__header-spacer" />
            <div class="dki-sheet__title">{{ $t("select-panel") }}</div>
            <button
              type="button"
              class="dki-sheet__back"
              @click="panelsListDialog = false"
            >
              <v-icon size="22" color="#37474F">mdi-close</v-icon>
            </button>
          </div>
          <div class="dki-sheet__body">
            <template v-if="items.length > 0">
              <p class="dki-sheet__hint">
                {{ $t("To connect to this product, choose from the panels below") }}
              </p>
              <button
                v-for="(item, index) in items"
                :key="'p3-pick-' + index"
                type="button"
                class="dki-sheet__row"
                :class="{ 'dki-sheet__row--disabled': item.isSelected }"
                :disabled="item.isSelected"
                @click="selectPanel(item)"
              >
                <span class="dki-sheet__row-index">{{ index + 1 }}</span>
                <span class="dki-sheet__row-name">{{ item.panel_name }}</span>
                <span
                  class="dki-sheet__row-status"
                  :style="{
                    color: item.isSelected
                      ? 'silver'
                      : item.panel_status == 'active'
                        ? '#137333'
                        : '#ff5252',
                  }"
                >
                  {{ $t(item.panel_status) }}
                </span>
              </button>
            </template>
            <p v-else class="dki-sheet__hint">
              {{ $t("no panel founded!") }}
              {{ $t("first add panels in integrator setting") }}
            </p>
          </div>
        </v-card>
      </v-bottom-sheet>

      <!-- Product variants table -->
      <v-bottom-sheet v-model="tableDialog" scrollable>
        <v-card class="dki-sheet dki-sheet--tall" dir="rtl">
          <div class="dki-sheet__handle" />
          <div class="dki-sheet__header">
            <span class="dki-sheet__header-spacer" />
            <div class="dki-sheet__title">
              <template v-if="selectedPanel != null && tableDialogType == 'add'">
                {{ $t("product-infomation") }}
                {{ selectedPanel.panel_name || selectedPanel.name }}
              </template>
              <template
                v-else-if="selectedPanel != null && tableDialogType == 'edit'"
              >
                {{ $t("edit-product") }}
                {{ selectedPanel.panel_name || selectedPanel.name }}
              </template>
            </div>
            <button
              type="button"
              class="dki-sheet__back"
              :aria-label="$t('back')"
              @click="tableDialog = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
          <div class="dki-sheet__toolbar">
            <button
              type="button"
              class="dki-sheet__tool"
              @click="openProductDialog('add')"
            >
              <v-icon size="16" color="#5B7C99">mdi-plus</v-icon>
              <span>{{ $t("add-new-portal-id") }}</span>
            </button>
            <div class="dki-sheet__tool-group">
              <button
                type="button"
                class="dki-sheet__tool dki-sheet__tool--ok"
                @click="activeAllProduct"
              >
                {{ $t("active-all") }}
              </button>
              <button
                type="button"
                class="dki-sheet__tool dki-sheet__tool--err"
                @click="deactiveAllProduct"
              >
                {{ $t("deactive-all") }}
              </button>
            </div>
          </div>
          <div class="dki-sheet__body dki-sheet__body--scroll">
            <div
              v-if="!(tableItems && tableItems.length)"
              class="dki-mobile__empty"
            >
              <v-icon size="28" color="#90A4AE">mdi-barcode-off</v-icon>
              <div class="dki-mobile__empty-title">
                {{ $t("no-data") }}
              </div>
              <div class="dki-mobile__empty-hint">
                {{ $t("add-new-portal-id") }}
              </div>
            </div>
            <div
              v-for="(item, index) in tableItems"
              :key="'p3-var-' + index"
              class="dki-var-card"
            >
              <div class="dki-var-card__top">
                <span class="dki-var-card__index">{{ index + 1 }}</span>
                <span
                  class="dki-var-card__status"
                  :style="{
                    color: item.status == 'active' ? '#137333' : '#ff5252',
                  }"
                >
                  {{ $t(item.status) }}
                </span>
              </div>
              <div class="dki-var-card__row">
                <span>{{ $t("portalId") }}</span>
                <strong>{{ item.portal_id }}</strong>
              </div>
              <div class="dki-var-card__row">
                <span>{{ $t("portalVariantId") }}</span>
                <strong>{{ item.portal_dynamic_id }}</strong>
              </div>
              <div class="dki-var-card__actions">
                <button
                  type="button"
                  class="dki-var-card__btn"
                  @click="openProductDialog('edit', item)"
                >
                  <v-icon size="18" color="#546E7A">mdi-pencil-outline</v-icon>
                </button>
                <button
                  type="button"
                  class="dki-var-card__btn dki-var-card__btn--danger"
                  @click="openDeleteProductDialog('single', item, index)"
                >
                  <v-icon size="18" color="#E91E63">mdi-trash-can-outline</v-icon>
                </button>
              </div>
            </div>
          </div>
          <div class="dki-sheet__footer">
            <button
              v-if="tableItems && tableItems.length"
              type="button"
              class="dki-sheet__clear-all"
              @click="openDeleteProductDialog('all')"
            >
              <v-icon size="16" color="#E91E63">mdi-trash-can-outline</v-icon>
              <span>{{ $t("delete-all-items") }}</span>
            </button>
            <div class="dki-sheet__footer-row">
              <v-btn
                color="primary"
                height="48"
                class="dki-sheet__footer-btn"
                @click="processPanel"
              >
                {{ tableDialogType == "add" ? $t("confirm") : $t("edit") }}
              </v-btn>
              <v-btn
                outlined
                height="48"
                color="secondary"
                class="dki-sheet__footer-btn"
                @click="tableDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-bottom-sheet>

      <!-- Add/edit variant -->
      <v-bottom-sheet v-model="productDialog">
        <v-card class="dki-sheet" dir="rtl">
          <div class="dki-sheet__handle" />
          <div class="dki-sheet__header">
            <span class="dki-sheet__header-spacer" />
            <div class="dki-sheet__title">
              {{
                productDialogType == "add" ? $t("add-new-variant") : $t("edit")
              }}
            </div>
            <button
              type="button"
              class="dki-sheet__back"
              @click="productDialog = false"
            >
              <v-icon size="22" color="#37474F">mdi-close</v-icon>
            </button>
          </div>
          <v-form
            ref="form"
            v-model="valid"
            class="dki-sheet__body dki-sheet__body--form"
          >
            <div class="dki-status-switch">
              <span class="dki-status-switch__label">
                {{
                  selectedStatusProduct === "active"
                    ? $t("active")
                    : $t("deactive")
                }}
              </span>
              <v-switch
                class="ma-0 pa-0"
                dense
                inset
                hide-details
                :input-value="selectedStatusProduct === 'active'"
                color="#2E7D32"
                @change="onMobileStatusSwitch"
              />
            </div>
            <div class="dki-field__section">{{ $t("general-setting") }}</div>
            <div class="dki-field">
              <div class="dki-field__label">{{ $t("portalId") }}</div>
              <input
                v-model="newProduct.portal_id"
                type="text"
                class="dki-field__input"
                :class="{ 'dki-field__input--error': mobileFieldError }"
                :placeholder="$t('portalId')"
                autocomplete="off"
              />
            </div>
            <div class="dki-field">
              <div class="dki-field__label">{{ $t("portalVariantId") }}</div>
              <input
                v-model="newProduct.portal_dynamic_id"
                type="text"
                class="dki-field__input"
                :placeholder="$t('portalVariantId')"
                autocomplete="off"
              />
            </div>
            <div v-if="mobileFieldError" class="dki-field__error">
              {{ $t("is-required") }}
            </div>
          </v-form>
          <div class="dki-sheet__footer dki-sheet__footer--stack">
            <v-btn color="primary" block height="48" @click="checkValidation">
              {{ productDialogType == "add" ? $t("add") : $t("edit") }}
            </v-btn>
            <v-btn
              outlined
              block
              height="48"
              color="secondary"
              @click="productDialog = false"
            >
              {{ $t("cancel") }}
            </v-btn>
          </div>
        </v-card>
      </v-bottom-sheet>

      <!-- Delete confirm -->
      <v-bottom-sheet
        v-if="deleteItem != null || deleteTpye == 'all'"
        v-model="deleteProductDialog"
      >
        <v-card class="dki-sheet" dir="rtl">
          <div class="dki-sheet__handle" />
          <div class="dki-sheet__icon">
            <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
          </div>
          <div class="dki-sheet__title dki-sheet__title--center">
            {{ $t("delete") }}
          </div>
          <p class="dki-sheet__subtitle">
            {{
              deleteTpye == "single"
                ? $t("Are you sure you want to delete this item ?")
                : $t("are you sure you want to delete all items?")
            }}
          </p>
          <div
            v-if="deleteTpye == 'single' && deleteItem"
            class="dki-sheet__info"
          >
            <div class="dki-sheet__info-row">
              <span>{{ $t("sazitoId") }}</span>
              <strong>{{ deleteItem.sazito_id }}</strong>
            </div>
            <div class="dki-sheet__info-row">
              <span>{{ $t("sazitoVariantId") }}</span>
              <strong>{{ deleteItem.portal_dynamic_id }}</strong>
            </div>
            <div class="dki-sheet__info-row">
              <span>{{ $t("status") }}</span>
              <strong>{{ $t(deleteItem.status) }}</strong>
            </div>
          </div>
          <div class="dki-sheet__footer">
            <v-btn color="error" block height="48" @click="deleteProduct">
              {{ $t("yes") }}
            </v-btn>
            <v-btn
              outlined
              block
              height="48"
              color="secondary"
              class="mt-2"
              @click="deleteProductDialog = false"
            >
              {{ $t("no") }}
            </v-btn>
          </div>
        </v-card>
      </v-bottom-sheet>
    </template>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      panelsListDialog: false,
      loading: false,
      items: [],
      addedPanels: [],
      tableDialog: false,
      selectedPanel: null,
      selectedPanelIndex: null,
      productDialog: false,
      selectedStatusProduct: null,
      newProduct: {
        panel_id: null,
        product_id: null,
        portal_id: null,
        portal_dynamic_id: null,
        status: null,
      },
      tableHeaders: [],
      tableItems: [],
      valid: true,
      productDialogType: null,
      editedIndex: null,
      deleteProductDialog: false,
      deleteTpye: null,
      deleteItem: null,
      deletedIndex: null,
      productVariant: null,
      tableDialogType: null,
      mobileFieldError: false,
      /* userCanAddSazitoId: true */
    };
  },
  props: {
    portalPanel: Array,
    mobile: { type: Boolean, default: false },
  },
  computed: {
    activatorOptions() {
      return [
        { label: this.$t("active"), value: "active" },
        { label: this.$t("deactive"), value: "deactive" },
      ];
    },
  },
  methods: {
    openPanelsList() {
      this.fetchPanelList();
    },
    fetchPanelList() {
      this.loading = true;
      var app = this;
      app.$axios
        .get(this.$endpoint + "integrator/portal/list")
        .then((r) => {
          this.loading = false;
          this.items = r.data;

          this.items.forEach((item) => {
            item.isSelected = this.addedPanels.some((panel) =>
              panel.panel_name === undefined
                ? panel.name === item.panel_name && panel.status === item.panel_status
                : panel.panel_name === item.panel_name &&
                  panel.panel_status === item.panel_status
            );
          });
          this.panelsListDialog = true;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        });
    },
    selectPanel(item) {
      if (!item.isSelected) {
        this.selectedPanel = item;
        this.panelsListDialog = false;
        this.openTableDialog("add");
      }
    },
    openTableDialog(type, item, index) {
      this.tableDialog = true;
      this.tableDialogType = type;
      if (type == "add") {
        /* this.userCanAddSazitoId = true */
        this.tableItems = [];
      }
      if (type == "edit" && item != null && index != null) {
        this.selectedPanel = item;
        this.selectedPanelIndex = index;
        /* console.log('salam' + JSON.stringify(item)) */
        this.tableItems = item.sazito_product;
        /* if (this.tableItems.length > 0) {
          this.userCanAddSazitoId = false
        } */
        /* console.log('khobi' + JSON.stringify(this.tableItems)) */
      }
    },
    openProductDialog(type, item) {
      this.productDialog = true;
      this.productDialogType = type;
      if (type == "add") {
        this.selectedStatusProduct = "active";
        this.newProduct.status = this.selectedStatusProduct;
      }
      if (type == "edit" && item != null) {
        this.newProduct = {
          panel_id: item.panel_id,
          product_id: item.product_id ? item.product_id : null,
          portal_id: item.portal_id,
          portal_dynamic_id: item.portal_dynamic_id,
          status: item.status,
        };
        this.selectedStatusProduct = item.status;
        this.editedIndex = this.tableItems.findIndex(
          (obj) =>
            obj.portal_dynamic_id == item.portal_dynamic_id &&
            obj.portal_id == item.portal_id
        );
      }
    },
    checkValidation() {
      if (this.mobile) {
        const portalId = String(this.newProduct.portal_id || "").trim();
        if (!portalId) {
          this.mobileFieldError = true;
          return;
        }
        this.mobileFieldError = false;
        if (this.productDialogType == "add") this.addProduct();
        if (this.productDialogType == "edit") this.editProduct();
        return;
      }
      if (this.$refs.form.validate() && this.productDialogType == "add") {
        this.addProduct();
      }
      if (this.$refs.form.validate() && this.productDialogType == "edit") {
        this.editProduct();
      }
    },
    addProduct() {
      this.tableItems.push(this.newProduct);
      this.productDialog = false;
      /* if (this.tableItems.length > 0) {
        this.userCanAddSazitoId = false
      } */
      // console.log('table after add' + JSON.stringify(this.tableItems))
    },
    editProduct() {
      this.tableItems.splice(this.editedIndex, 1, this.newProduct);
      this.productDialog = false;
      // console.log('table after edit' + JSON.stringify(this.tableItems))
    },
    openDeleteProductDialog(type, item, index) {
      this.deleteProductDialog = true;
      this.deleteTpye = type;
      if (this.deleteTpye == "single" && item != null && index != null) {
        this.deleteItem = item;
        this.deletedIndex = index;
      }
    },
    deleteProduct() {
      if (this.deleteTpye == "single") {
        this.tableItems.splice(this.deletedIndex, 1);
        // console.log('table after delete single' + JSON.stringify(this.tableItems))
      }
      if (this.deleteTpye == "all") {
        this.tableItems = [];
        // console.log('table after delete all' + JSON.stringify(this.tableItems))
      }
      this.deleteProductDialog = false;
      /* if (this.tableItems.length == 0) {
        this.userCanAddSazitoId = true
      } */
    },
    processPanel() {
      if (this.tableDialogType == "add") {
        this.addPanel();
      }
      if (this.tableDialogType == "edit") {
        this.editPanel();
      }
    },
    addPanel() {
      if (this.tableItems.length > 0) {
        if (!this.selectedPanel.sazito_product && !this.selectedPanel.productVariant) {
          this.$set(this.selectedPanel, "sazito_product", []);
          this.$set(this.selectedPanel, "productVariant", this.tableItems.length);
        }
        this.selectedPanel.sazito_product.push(...this.tableItems);
        this.addedPanels.push(this.selectedPanel);
        console.log(this.addedPanels);
        this.tableDialog = false;
        this.$emit("updateSazito", this.addedPanels);
      } else {
        this.tableDialog = false;
      }
    },
    editPanel() {
      if (this.tableItems.length > 0) {
        if (this.selectedPanel.sazito_product && this.selectedPanel.productVariant) {
          this.selectedPanel.sazito_product = this.tableItems;
          this.selectedPanel.productVariant = this.tableItems.length;
          this.addedPanels.splice(this.selectedPanelIndex, 1, this.selectedPanel);
          this.$emit("updatePortal", this.addedPanels);
          this.tableDialog = false;
        }
        if (!this.selectedPanel.sazito_product && !this.selectedPanel.productVariant) {
          this.$set(this.selectedPanel, "sazito_product", []);
          this.$set(this.selectedPanel, "productVariant", this.tableItems.length);
          this.selectedPanel.sazito_product = this.tableItems;
          this.selectedPanel.productVariant = this.tableItems.length;
          this.addedPanels.splice(this.selectedPanelIndex, 1, this.selectedPanel);
          this.$emit("updatePortal", this.addedPanels);
          this.tableDialog = false;
        }
        if (!this.selectedPanel.sazito_product || this.selectedPanel.sazito_product) {
          this.selectedPanel.sazito_product = this.tableItems;
          this.addedPanels.splice(this.selectedPanelIndex, 1, this.selectedPanel);
          this.$emit("updatePortal", this.addedPanels);
          this.tableDialog = false;
        }
      } else {
        this.addedPanels.splice(this.selectedPanelIndex, 1);
        this.$emit("updatePortal", this.addedPanels);
        this.tableDialog = false;
      }
    },
    selectStatusProduct(item) {
      this.selectedStatusProduct = item.value;
      this.newProduct.status = item.value;
    },
    onMobileStatusSwitch(val) {
      const status = val ? "active" : "deactive";
      this.selectedStatusProduct = status;
      this.newProduct.status = status;
    },
    activeAllProduct() {
      this.tableItems = this.tableItems.map((item) => ({ ...item, status: "active" }));
      this.selectedStatusProduct = "active";
      // console.log('table after active all' + JSON.stringify(this.tableItems))
    },
    deactiveAllProduct() {
      this.tableItems = this.tableItems.map((item) => ({ ...item, status: "deactive" }));
      this.selectedStatusProduct = "deactive";
      // console.log('table after deactive all' + JSON.stringify(this.tableItems))
    },
  },
  beforeMount() {
    this.tableHeaders = [
      {
        text: this.$t("portalId"),
        align: "start",
        sortable: false,
        value: "portal-id",
      },
      {
        text: this.$t("portalVariantId"),
        align: "start",
        sortable: false,
        value: "portal-dynamic-id",
      },
      {
        text: this.$t("other"),
        align: "start",
        sortable: false,
        value: "other",
      },
      {
        text: this.$t("edit"),
        align: "start",
        sortable: false,
        value: "edit",
      },
    ];
  },
  mounted() {
    this.userInfo = JSON.parse(localStorage.getItem("token"));
  },
  watch: {
    productDialog(val) {
      if (!val) {
        // reset keys
        this.newProduct = {
          panel_id: this.selectedPanel ? this.selectedPanel.id : null,
          product_id: null,
          portal_id: null,
          portal_dynamic_id: null,
          status: null,
        };
        this.mobileFieldError = false;
        // reset form
        if (this.$refs.form && typeof this.$refs.form.reset === "function") {
          this.$refs.form.reset();
        }
        // reset toggle button
        this.selectedStatusProduct = null;
      }
    },
    selectedPanel(newVal) {
      this.newProduct.panel_id = newVal ? newVal.id : null;
    },
    portalPanel: {
      handler(val) {
        if (val && val.length > 0) {
          // console.log('final: ' + JSON.stringify(val))
          this.addedPanels = val;
        } else {
          this.addedPanels = [];
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped>
.dki-mobile__shell {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dki-mobile__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.dki-mobile__add:active {
  background: #dde4ef;
}
.dki-mobile__add:disabled {
  opacity: 0.65;
  cursor: default;
}
.dki-mobile__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 28px 16px;
  text-align: center;
  border: 1px dashed #d5dde5;
  border-radius: 12px;
  background: #f7f9fc;
}
.dki-mobile__empty-title {
  font-size: 14px;
  font-weight: 800;
  color: #546e7a;
  line-height: 1.4;
}
.dki-mobile__empty-hint {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  line-height: 1.5;
}
.dki-mobile__panel-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
}
.dki-mobile__panel-card:active {
  background: #f5f7f9;
}
.dki-mobile__panel-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.dki-mobile__panel-index {
  flex: 0 0 auto;
  width: 22px;
  font-size: 12px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.dki-mobile__panel-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.dki-mobile__panel-name {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dki-mobile__panel-meta {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.dki-sheet {
  border-radius: 20px 20px 0 0 !important;
  background: #fff !important;
  padding: 8px 0 calc(12px + env(safe-area-inset-bottom, 0px));
}
.dki-sheet--tall {
  max-height: min(92vh, 760px);
  display: flex;
  flex-direction: column;
}
.dki-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 10px;
  border-radius: 999px;
  background: #cfd8dc;
}
.dki-sheet__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 10px;
}
.dki-sheet__header-spacer,
.dki-sheet__back {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
}
.dki-sheet__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.dki-sheet__title {
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  color: #263238;
  line-height: 1.35;
}
.dki-sheet__title--center {
  margin: 0 16px 8px;
}
.dki-sheet__subtitle {
  margin: 0 16px 14px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.dki-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.dki-sheet__body {
  padding: 0 16px 12px;
}
.dki-sheet__body--scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.dki-sheet__hint {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.5;
}
.dki-sheet__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  text-align: right;
  cursor: pointer;
}
.dki-sheet__row--disabled {
  background: #f1f3f5;
  cursor: default;
  opacity: 0.75;
}
.dki-sheet__row-index {
  flex: 0 0 20px;
  font-size: 12px;
  font-weight: 700;
  color: #90a4ae;
}
.dki-sheet__row-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
}
.dki-sheet__row-status {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
}
.dki-sheet__toolbar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 10px;
}
.dki-sheet__tool-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.dki-sheet__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #c5d0da;
  border-radius: 10px;
  background: #fff;
  color: #546e7a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.dki-sheet__tool--ok {
  border-color: #a5d6a7;
  color: #2e7d32;
  background: #f1f8f2;
}
.dki-sheet__tool--err {
  border-color: #ef9a9a;
  color: #c62828;
  background: #fff5f5;
}
.dki-sheet__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 16px 0;
}
.dki-sheet__footer-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.dki-sheet__footer-btn {
  width: 100% !important;
  margin: 0 !important;
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
.dki-sheet__footer--stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dki-sheet__body--form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
}
.dki-sheet__clear-all {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  margin-top: 8px;
  border: 1px dashed #f8bbd0;
  border-radius: 10px;
  background: #fce4ec;
  color: #c2185b;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.dki-sheet__info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 14px;
}
.dki-sheet__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.dki-sheet__info-row strong {
  color: #263238;
  font-weight: 800;
}
.dki-var-card {
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  margin-bottom: 10px;
}
.dki-var-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.dki-var-card__index {
  font-size: 12px;
  font-weight: 700;
  color: #90a4ae;
}
.dki-var-card__status {
  font-size: 12px;
  font-weight: 700;
}
.dki-var-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.dki-var-card__row strong {
  font-size: 13px;
  font-weight: 800;
  color: #37474f;
}
.dki-var-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.dki-var-card__btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}
.dki-var-card__btn--danger {
  border-color: #f8bbd0;
  background: #fce4ec;
}
.dki-field {
  display: flex;
  flex-direction: column;
  margin: 0;
  gap: 0;
}
.dki-field__section {
  margin: 0 2px;
  font-size: 13px;
  font-weight: 800;
  color: #546e7a;
}
.dki-field__label {
  display: block;
  flex: 0 0 auto;
  margin: 0 2px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  line-height: 1.3;
}
.dki-field__input {
  width: 100%;
  height: 40px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  color: #37474f;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
  box-sizing: border-box;
  outline: none;
}
.dki-field__input:focus {
  border-color: #90caf9;
  background: #e3f2fd;
}
.dki-field__input--error {
  border-color: #ef9a9a;
}
.dki-field__error {
  margin: -8px 2px 0;
  font-size: 12px;
  font-weight: 600;
  color: #e53935;
}
.dki-status-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  padding: 4px 4px 4px 2px;
  margin: 0 0 4px;
  border-bottom: 1px solid #eef2f5;
}
.dki-status-switch__label {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
}
.wrapper {
  background-color: #fcfcfc;
}
.columns .column-panel {
  border: 1px solid silver;
  border-radius: 4px;
  cursor: pointer;
}
.columns .column-panel-disabled {
  background-color: #c0c0c047;
  border-radius: 4px;
  cursor: auto;
}
.v-card__text {
  width: auto;
}
.wrapper-toggle {
  border: 1px solid silver;
  border-radius: 4px;
}
.wrapper-toggle .child {
  padding: 7.7px;
  cursor: pointer;
  border-radius: 4px;
}
.wrapper-toggle .child:first-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.wrapper-toggle .child:nth-child(2) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-left: 1px;
}
::v-deep .v-data-table.product .v-data-table-header {
  background-color: #d9d9d9;
}
::v-deep .v-data-table.product {
  border: 1px solid silver;
}
::v-deep .v-data-table.product .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::v-deep .v-data-table.product .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: silver;
}
::v-deep .v-data-table.product .v-data-table__wrapper::-webkit-scrollbar-track {
  background-color: #f1f1f1;
}
::v-deep .v-data-table.product > .v-data-table__wrapper > table > thead > tr > th {
  font-size: 8.3pt;
  border-left: 1px solid silver;
  padding: 0 9px;
  color: black;
  height: 40px !important;
}
::v-deep
  .v-data-table.product
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:first-child {
  border-top-right-radius: 4px;
}
::v-deep
  .v-data-table.product
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:last-child {
  padding: 0 8px 0 4px;
  width: 30px;
  border-left: none;
  border-top-left-radius: 4px;
}
::v-deep .v-data-table.product > .v-data-table__wrapper > table > tbody {
  position: relative;
}
::v-deep .v-data-table.product > .v-data-table__wrapper > table > tbody > tr > td {
  border-left: 1px solid silver;
  padding-right: 10px;
}
::v-deep
  .v-data-table
  > .v-data-table__wrapper
  tbody
  tr:first-child:hover
  td:first-child {
  border-top-left-radius: 0;
}
::v-deep .v-data-table > .v-data-table__wrapper tbody tr:first-child:hover td:last-child {
  border-top-right-radius: 0;
}
::v-deep .v-data-table > .v-data-table__wrapper tbody tr:last-child {
  border-top: 1px solid silver;
  background-color: #f6f6f6;
}
::v-deep .v-data-table > .v-data-table__wrapper tbody tr:nth-last-child(2) {
  background-color: #f6f6f6;
}
::v-deep
  .v-data-table.product
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:last-child {
  width: 0;
  border-left: none;
  padding: 0 8px 0 3.6pt;
}
.custom-input {
  position: relative;
}
</style>
